import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Eye, EyeOff, MapPin } from 'lucide-react';
import AddressAutocomplete from '../components/AddressAutocomplete';

const AU_STATES = [
  { value: 'NSW', label: 'New South Wales' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'QLD', label: 'Queensland' },
  { value: 'WA', label: 'Western Australia' },
  { value: 'SA', label: 'South Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'ACT', label: 'Australian Capital Territory' },
  { value: 'NT', label: 'Northern Territory' },
];

const NZ_REGIONS = [
  { value: 'AUK', label: 'Auckland' },
  { value: 'WGN', label: 'Wellington' },
  { value: 'CAN', label: 'Canterbury' },
  { value: 'WKO', label: 'Waikato' },
  { value: 'BOP', label: 'Bay of Plenty' },
  { value: 'OTA', label: 'Otago' },
  { value: 'MWT', label: 'Manawatu-Wanganui' },
  { value: 'NTL', label: 'Northland' },
  { value: 'HKB', label: "Hawke's Bay" },
  { value: 'TKI', label: 'Taranaki' },
  { value: 'GIS', label: 'Gisborne' },
  { value: 'STL', label: 'Southland' },
  { value: 'MBH', label: 'Marlborough' },
  { value: 'NSN', label: 'Nelson' },
  { value: 'TAS', label: 'Tasman' },
  { value: 'WTC', label: 'West Coast' },
];

export default function RegisterPage() {
  const { register } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    company_name: '',
    // Shipping address
    address_street: '',
    address_city: '',
    address_state: '',
    address_postcode: '',
    address_country: 'Australia',
  });

  const handleAddressSelect = (addressData) => {
    setForm(prev => ({
      ...prev,
      address_street: addressData.street,
      address_city: addressData.city,
      address_state: addressData.state,
      address_postcode: addressData.postcode,
      address_country: addressData.country || 'Australia',
    }));
  };

  const isNZ = form.address_country === 'New Zealand';
  const stateOptions = isNZ ? NZ_REGIONS : AU_STATES;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Format address for backend
      const registrationData = {
        email: form.email,
        password: form.password,
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone,
        company_name: form.company_name || null,
        addresses: [{
          type: 'shipping',
          street: form.address_street,
          city: form.address_city,
          state: form.address_state,
          postcode: form.address_postcode,
          country: form.address_country,
          is_default: true,
        }]
      };
      await register(registrationData);
      toast.success('Account created successfully');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-16" data-testid="register-page">
        <div className="w-full max-w-lg">
          <div className="bg-card border border-border rounded-sm p-8">
            <h1 className="text-2xl font-black text-foreground uppercase tracking-tight text-center mb-6">
              Create Account
            </h1>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Personal Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    className="mt-1 bg-background border-border"
                    required
                    data-testid="first-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    className="mt-1 bg-background border-border"
                    required
                    data-testid="last-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 bg-background border-border"
                  required
                  data-testid="email-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-1 bg-background border-border"
                    placeholder="04XX XXX XXX"
                    required
                    data-testid="phone-input"
                  />
                </div>
                <div>
                  <Label htmlFor="company_name">Company (Optional)</Label>
                  <Input
                    id="company_name"
                    value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    className="mt-1 bg-background border-border"
                    data-testid="company-input"
                  />
                </div>
              </div>

              {/* Shipping Address */}
              <div className="border-t border-border pt-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="h-4 w-4 text-[#78BE20]" />
                  <Label className="text-sm font-semibold uppercase text-muted-foreground">Shipping Address</Label>
                </div>
                
                <div className="space-y-3">
                  {/* Address Autocomplete */}
                  <div>
                    <Label>Search Address</Label>
                    <AddressAutocomplete 
                      onAddressSelect={handleAddressSelect}
                      placeholder="Start typing your address..."
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Type your address to auto-fill (AU & NZ)
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="address_street">Street Address *</Label>
                    <Input
                      id="address_street"
                      value={form.address_street}
                      onChange={(e) => setForm({ ...form, address_street: e.target.value })}
                      className="mt-1 bg-background border-border"
                      placeholder="123 Main Street"
                      required
                      data-testid="address-street-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="address_city">City / Suburb *</Label>
                      <Input
                        id="address_city"
                        value={form.address_city}
                        onChange={(e) => setForm({ ...form, address_city: e.target.value })}
                        className="mt-1 bg-background border-border"
                        required
                        data-testid="address-city-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="address_postcode">Postcode *</Label>
                      <Input
                        id="address_postcode"
                        value={form.address_postcode}
                        onChange={(e) => setForm({ ...form, address_postcode: e.target.value })}
                        className="mt-1 bg-background border-border"
                        placeholder={isNZ ? "1010" : "2000"}
                        maxLength={isNZ ? 4 : 4}
                        required
                        data-testid="address-postcode-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="address_state">{isNZ ? 'Region' : 'State'} *</Label>
                      <Select 
                        value={form.address_state} 
                        onValueChange={(v) => setForm({ ...form, address_state: v })}
                        required
                      >
                        <SelectTrigger className="mt-1 bg-background border-border" data-testid="address-state-select">
                          <SelectValue placeholder={isNZ ? "Select region" : "Select state"} />
                        </SelectTrigger>
                        <SelectContent>
                          {stateOptions.map(state => (
                            <SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="address_country">Country</Label>
                      <Input
                        id="address_country"
                        value={form.address_country}
                        className="mt-1 bg-background border-border text-muted-foreground"
                        disabled
                        data-testid="address-country-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Password */}
              <div className="border-t border-border pt-4 mt-4">
                <Label htmlFor="password">Password *</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="bg-background border-border pr-10"
                    required
                    minLength={6}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Minimum 8 characters</p>
              </div>

              <Button
                type="submit"
                disabled={loading || !form.address_state}
                className="w-full bg-[#78BE20] text-black hover:bg-[#65a31b] h-12 mt-6"
                data-testid="register-submit"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>

            <p className="text-center text-muted-foreground text-sm mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-[#78BE20] hover:underline">
                Login
              </Link>
            </p>

            <p className="text-center text-muted-foreground text-xs mt-4">
              Need trade pricing?{' '}
              <Link to="/trade-application" className="text-[#78BE20] hover:underline">
                Apply for a Trade Account
              </Link>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
